import React from "react";
import { Typography } from "common/components/Typography";
import { ContentCard } from "../../../components/ContentCard";
import { SimilarListings } from "common/types/common.types";
import { Chart } from "../../../components/charts/Chart";
import {
  createMarketDaysChartData,
  createSquareMeterPriceChartData,
  getAvgDaysToSellListing,
} from "../../../helpers/similarProperties.helpers";

import { ListingsWithMap } from "./ListingsWithMap";
import { AddonListingCard } from "../../../components/comparablesSummary/AddonListingCard";
import { AppraiserProperty } from "../../../types/appraiser.types";
import colors from "common/styles/colors";
import {
  ClusteredGenericMapControlProvider,
  ClusteredListingsMapControlContext,
} from "../../../components/ClusteredGenericMapControlProvider";
import { getPropertyListString } from "../../../helpers/property.helpers";
import { ZonePolygonProvider } from "./ZonePolygonProvider";
import { useTranslation } from "react-i18next";
import { Divider, Stack } from "@mui/material";
import { ChartData } from "../../../types/api.types";
import { formatAreaPrice, formatPrice } from "common/helpers/text.helpers";

const Section = ({
  headline,
  body,
  chartData,
}: {
  headline?: string;
  body: { leftText: string; mainValue?: string; secondaryValue?: string };
  chartData: ChartData;
}) => {
  return (
    <Stack>
      {!!headline && (
        <Typography
          textStyle={{
            fontSize: 20,
            letterSpacing: 0,
            paddingBottom: 30,
            lineHeight: 28,
          }}
        >
          {headline}
        </Typography>
      )}
      <Typography
        textStyle={{
          fontSize: 16,
          fontWeight: "400",
          lineHeight: 20,
          paddingBottom: "4px",
        }}
      >
        {body.leftText}{" "}
        <div style={{ color: colors.purple2, display: "inline" }}>
          <strong>
            {body.mainValue}
            {body.mainValue ? " " : ""}
          </strong>
          {body.secondaryValue}
        </div>
      </Typography>
      <Chart
        chartData={chartData}
        tickMinStep={1}
        titleStyle={{
          fontWeight: 400,
          fontSize: "16px",
          lineHeight: "20px",
        }}
        chartWrapperStyle={{
          borderRadius: "10px",
          border: `1px solid ${colors.greybg}`,
          marginBottom: "9px",
        }}
      />
    </Stack>
  );
};

interface SimilarPropertiesInsightsProps {
  property: AppraiserProperty | undefined;
  similarListings: SimilarListings;
}

export const SimilarPropertiesInsights: React.FC<
  SimilarPropertiesInsightsProps
> = ({ property, similarListings }) => {
  const { t } = useTranslation();

  const marketDaysChartData = createMarketDaysChartData(
    similarListings.stats.sold_days_on_market || null,
    t
  );
  const avgDaysToSell = getAvgDaysToSellListing(
    similarListings.stats.sold_days_on_market || null
  );
  const sqmPriceChartData = createSquareMeterPriceChartData(
    similarListings.stats.map_square_meter_price || null,
    t
  );
  const sqmPriceMedian = similarListings.stats.map_square_meter_price.median;
  const averagePriceBySqm = property?.area
    ? property.area * sqmPriceMedian
    : undefined;

  return (
    <ContentCard title={t("propertyRoute.similarPropertiesOnMarket")}>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "40px",
          marginBottom: "40px",
          pageBreakAfter: "always",
        }}
      >
        {marketDaysChartData && avgDaysToSell && (
          <Section
            headline={t("similarListings.daysOnMarket.headLine")}
            body={{
              leftText: t("similarListings.daysOnMarket.summary"),
              mainValue: `${avgDaysToSell} ${t("valuationsStatistics.days")}`,
            }}
            chartData={marketDaysChartData}
          />
        )}
        {!!sqmPriceChartData && !!sqmPriceMedian && !!property && (
          <Section
            headline={t("similarListings.areaPrice.headLine")}
            chartData={sqmPriceChartData}
            body={{
              leftText: t("similarListings.areaPrice.summary"),
              mainValue: `${formatPrice(averagePriceBySqm)}`,
              secondaryValue: `(${formatAreaPrice(sqmPriceMedian)})`,
            }}
          />
        )}
      </div>
      <ClusteredGenericMapControlProvider
        context={ClusteredListingsMapControlContext}
      >
        <Typography
          textStyle={{
            lineHeight: 30,
            fontSize: 20,
            fontWeight: "600",
            paddingTop: "27px", // in pdf do not stick to very top
            display: "flex",
          }}
        >
          {t("similarListings.titleMap")}
        </Typography>
        <Divider style={{ paddingTop: "30px", marginBottom: "30px" }} />
        <ZonePolygonProvider
          usedZones={similarListings.used_zones}
          coeffName={similarListings.coeff_name}
          ineffectiveZones={similarListings.ineffective_zones}
        >
          <ListingsWithMap
            listings={similarListings.listings}
            mapCardComponent={(
              listing,
              paginationNode,
              onClick,
              badgeNum?: number
            ) => (
              <AddonListingCard
                listing={listing}
                price={listing.sold_price ?? 0}
                onClick={onClick}
                paginationNode={paginationNode}
                caption={getPropertyListString({
                  finishing: Boolean(listing.finishing),
                  rooms: listing.rooms,
                  area: listing.area,
                  floorNr:
                    listing.floor /* or should it be listing.floor_number? */,
                  floorCount: listing.floor_total,
                  year: listing.construction_year,
                  renovationYear: listing.reconstruction_year,
                })}
                numBadgeProps={
                  badgeNum === undefined ? undefined : { count: badgeNum }
                }
              />
            )}
            mapPriceFn={(listing) => listing.sold_price ?? 0}
            property={property}
          />
        </ZonePolygonProvider>
      </ClusteredGenericMapControlProvider>
    </ContentCard>
  );
};
